import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import InfiniteScroll from 'react-infinite-scroller';
import { AppContext } from '../../App';
import {
  getPendingEnrichments,
  runEnrichmentNow,
} from '../../clients/product_enrichment_client';
import ProductEnrichmentListTable from '../../components/ProductEnrichment/ProductEnrichmentListTable';
import ProductEnrichmentDetailModal from '../../components/ProductEnrichment/ProductEnrichmentDetailModal';
import GlassWorkspaceShell from '../../components/Glass/GlassWorkspaceShell';
import { showToast } from '../../config_and_helpers/helpers';
import { logError, logInfo } from '../../config_and_helpers/logger';
import { INFINITE_SCROLL_THRESHOLD_PX } from '../../utils/listSearchRequest';

const LOG_CONTEXT = 'ProductEnrichmentPage';

/**
 * Lab-only queue for reviewing AI-generated product enrichment suggestions.
 */
const ProductEnrichmentPage = () => {
  const { token } = useContext(AppContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = searchParams.get('open');

  const [rows, setRows] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isRunningBatch, setIsRunningBatch] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  const loadPage = useCallback(
    async (nextPage = null, replace = false) => {
      if (!token) return;
      if (isLoadingRef.current && !replace) return;

      isLoadingRef.current = true;
      setIsLoading(true);

      const result = await getPendingEnrichments(token, { nextPage });

      if (!isMountedRef.current) return;

      isLoadingRef.current = false;
      setIsLoading(false);

      if (!result.success) {
        showToast(
          result.messages?.[0] || 'Failed to load enrichment queue',
          'error',
          'top-right',
          5000
        );
        logError(LOG_CONTEXT, 'load failed', {});
        return;
      }

      setRows(prev => {
        if (replace) return result.results || [];
        const ids = new Set(prev.map(r => r.id));
        const added = (result.results || []).filter(r => !ids.has(r.id));
        return [...prev, ...added];
      });
      setNextPageUrl(result.reachedEnd ? null : result.nextPage);
      setHasMore(!result.reachedEnd);
      hasMoreRef.current = !result.reachedEnd;

      logInfo(LOG_CONTEXT, 'load success', {
        replace,
        count: result.results?.length ?? 0,
        reachedEnd: result.reachedEnd,
      });
    },
    [token]
  );

  const refresh = useCallback(() => {
    setRows([]);
    setNextPageUrl(null);
    setHasMore(true);
    hasMoreRef.current = true;
    loadPage(null, true);
  }, [loadPage]);

  useEffect(() => {
    isMountedRef.current = true;
    refresh();
    return () => {
      isMountedRef.current = false;
    };
  }, [refresh]);

  const openParamHandledRef = useRef(false);

  useEffect(() => {
    if (!openParam || !token || openParamHandledRef.current) return;
    const id = parseInt(openParam, 10);
    if (Number.isNaN(id)) return;
    openParamHandledRef.current = true;
    setSelectedId(id);
    setSelectedRow(rows.find(r => r.id === id) || null);
    setDetailOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    setSearchParams(next, { replace: true });
  }, [openParam, token, rows, searchParams, setSearchParams]);

  const handleRowClick = row => {
    setSelectedRow(row);
    setSelectedId(row.id);
    setDetailOpen(true);
  };

  const handleResolved = resolvedId => {
    setRows(prev => prev.filter(r => r.id !== resolvedId));
  };

  const handleRunNow = async () => {
    if (!token || isRunningBatch) return;
    setIsRunningBatch(true);
    logInfo(LOG_CONTEXT, 'run-now clicked', {});
    const result = await runEnrichmentNow(token);
    setIsRunningBatch(false);
    if (result.success) {
      showToast('Enrichment batch started', 'success', 'top-right', 4000);
      refresh();
    } else if (result.status === 403) {
      showToast('Staff only — manual batch not available', 'warning', 'top-right', 5000);
    } else {
      showToast(
        result.messages?.[0] || 'Run now failed',
        'error',
        'top-right',
        5000
      );
    }
  };

  const loadMore = () => {
    if (!hasMoreRef.current || isLoadingRef.current || !nextPageUrl) return;
    loadPage(nextPageUrl, false);
  };

  return (
    <GlassWorkspaceShell theme="rose" wide>
      <div className="glass-panel mb-3">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
          <h2 className="glass-page-heading mb-0">Product enrichment review</h2>
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={refresh}
              disabled={isLoading}
            >
              Refresh
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleRunNow}
              disabled={isRunningBatch}
            >
              {isRunningBatch ? 'Running…' : 'Run now'}
            </Button>
          </div>
        </div>
        <p className="glass-page-sub mb-0">
          Review AI-generated product suggestions from the nightly enrichment job.
        </p>
      </div>

      {isLoading && rows.length === 0 ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          <div className="mt-2 text-muted">Loading enrichment queue…</div>
        </div>
      ) : !rows.length ? (
        <div className="glass-empty">
          <div className="mb-3">
            <span role="img" aria-label="no enrichments" style={{ fontSize: '3rem' }}>
              ✨
            </span>
          </div>
          <h5 className="mb-2">No enrichments awaiting review</h5>
          <p className="mb-0">The nightly job runs at 02:00, or use Run now to trigger a batch.</p>
        </div>
      ) : (
        <InfiniteScroll
          pageStart={0}
          loadMore={loadMore}
          hasMore={hasMore && !isLoading}
          threshold={INFINITE_SCROLL_THRESHOLD_PX}
          loader={
            <div className="text-center py-2" key="enrichment-loader">
              <Spinner animation="border" size="sm" role="status" />
              <div className="mt-1">
                <small className="text-muted">Loading more suggestions…</small>
              </div>
            </div>
          }
          useWindow
        >
          <div className="glass-table-wrap">
            <ProductEnrichmentListTable rows={rows} onRowClick={handleRowClick} />
          </div>
        </InfiniteScroll>
      )}

      <ProductEnrichmentDetailModal
        show={detailOpen}
        listRow={selectedRow}
        suggestionId={selectedId}
        token={token}
        onClose={() => {
          setDetailOpen(false);
          setSelectedRow(null);
          setSelectedId(null);
        }}
        onResolved={handleResolved}
      />
    </GlassWorkspaceShell>
  );
};

export default ProductEnrichmentPage;
